import { CommonModule } from '@angular/common';
import {
  Component, EventEmitter, forwardRef, Input, OnChanges, Output, SimpleChanges, ViewChild, ElementRef, AfterViewInit
} from '@angular/core';
import { FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR, ControlValueAccessor, AbstractControl, ValidationErrors, Validator } from '@angular/forms';

export interface PhoneValue {
  /** E.164 (+1XXXXXXXXXX) when complete & valid, else null */
  e164: string | null;
  /** Displayed national number (formatted by your appPhoneFormat directive) */
  national: string;
  /** Raw national digits only (no spaces/dashes/parentheses) */
  rawDigits: string;
  /** Dial code currently shown in the prefix, e.g. "+1" */
  dialCode: string;
  /** True when national digits meet expected length (basic validity check) */
  isValid: boolean;
}

@Component({
  selector: 'app-phone-input',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './phone-input.component.html',
  styleUrl: './phone-input.component.scss',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => PhoneInputComponent), multi: true },
    { provide: NG_VALIDATORS,  useExisting: forwardRef(() => PhoneInputComponent), multi: true },
  ]
})
export class PhoneInputComponent {
  /** Prefix shown next to the input. Today fixed "+1"; later you can bind a country picker. */
  @Input() dialCode: string = '+1';
  /** Expected national length (NANP = 10). Customize per country as you expand. */
  @Input() nationalMaxLength = 10;
  /** If true, empty field is invalid. */
  @Input() required = false;
  @Input() isLight: boolean = false;

  /** Emits normalized E.164 (or null while incomplete). */
  @Output() e164Change = new EventEmitter<string | null>();
  /** Emits validity whenever it changes. */
  @Output() validChange = new EventEmitter<boolean>();

  /** Internal model bound to the input (your appPhoneFormat will style this). */
  phoneNumber = '';

  /** Accessibility helper id */
  descId = `phone-desc-${Math.random().toString(36).slice(2)}`;

  disabled = false;
  private lastEmitted?: string | null;   // last E.164 emitted (for churn guard)
  private onChange: (value: PhoneValue) => void = () => { };
  private onTouched: () => void = () => { };

  @ViewChild('phoneInput', { static: false }) phoneInputEl?: ElementRef<HTMLInputElement>;

  // --- ControlValueAccessor ---
  writeValue(value: PhoneValue | string | null): void {
    if (typeof value === 'string') {
      // Parent wrote E.164 or national string.
      this.applyExternalValue(value);
    } else if (value) {
      // Parent wrote a PhoneValue
      this.dialCode = value.dialCode || this.dialCode;
      this.phoneNumber = value.national || '';
      // Autofix if external gave us a full E.164 but our UI shows a prefix
      if (value.e164 && this.shouldStripDialFrom(value.e164)) {
        this.phoneNumber = this.toNationalDigits(value.e164);
      }
    } else {
      this.phoneNumber = '';
    }
    // After setting, emit current normalized value
    this.emitCurrent();
  }

  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }

  // --- Validator ---
  validate(_: AbstractControl): ValidationErrors | null {
    const { isValid, rawDigits } = this.compute();
    if (this.required && !rawDigits) return { required: true };
    if (rawDigits && !isValid) return { phone: 'invalid' };
    return null;
  }

  // --- Lifecycle ---
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dialCode'] && !changes['dialCode'].firstChange) {
      // If dial code changes (future country menu), re-normalize existing input.
      this.phoneNumber = this.toNationalDigits(this.phoneNumber);
      this.emitCurrent();
    }
  }

  ngAfterViewInit(): void {
    // Post-render pass to clean up any browser autofill that inserted +1
    setTimeout(() => {
      if (!this.phoneInputEl) return;
      const val = this.phoneInputEl.nativeElement.value;
      if (val) {
        const national = this.toNationalDigits(val);
        if (national && national !== this.phoneNumber) {
          this.phoneNumber = national;
          this.emitCurrent();
        }
      }
    });
  }

  // --- Template handlers ---
  onPhoneNumberEntry(raw: string) {
    // Hook for browser input/autofill; normalize before emitting to parents.
    const national = this.toNationalDigits(raw);
    this.phoneNumber = national;
    this.emitCurrent(/*soft*/ true);
  }

  onPhoneModelChange(raw: string) {
    // If you prefer (ngModelChange) wiring, this does the same as above
    this.onPhoneNumberEntry(raw);
  }

  onPhonePaste(e: ClipboardEvent) {
    const text = e.clipboardData?.getData('text') ?? '';
    if (!text) return;
    e.preventDefault();
    this.phoneNumber = this.toNationalDigits(text);
    this.emitCurrent();
  }

  onBlur() { this.onTouched(); this.emitCurrent(); }

  // --- Core normalization & emission ---
  /** Build the normalized value (digits, validity, e164) from current phoneNumber. */
  private compute(): { rawDigits: string; isValid: boolean; e164: string | null } {
    const digits = this.onlyDigits(this.phoneNumber);
    const isValid = digits.length === this.nationalMaxLength;
    const dc = this.dialDigits();
    const e164 = isValid ? `+${dc}${digits}` : null;
    return { rawDigits: digits, isValid, e164 };
  }

  /** Emit to CVA + outputs (with churn guards). */
  private emitCurrent(soft = false) {
    const { rawDigits, isValid, e164 } = this.compute();
    const value: PhoneValue = {
      e164,
      national: this.phoneNumber,
      rawDigits,
      dialCode: this.dialCode,
      isValid,
    };

    // CVA change
    this.onChange(value);

    // Outputs with guard
    if (e164 !== this.lastEmitted) {
      this.e164Change.emit(e164);
      this.lastEmitted = e164;
    }
    if (!soft) {
      this.validChange.emit(isValid || (!this.required && !rawDigits));
    }
  }

  /** Strip the dial code if present in raw input (autofill/paste) and return national digits. */
  private toNationalDigits(raw: string): string {
    if (!raw) return '';
    const digits = this.onlyDigits(this.withoutExtension(raw));
    const dc = this.dialDigits();

    if (!digits) return '';

    // Common international dialing prefixes for pasted/autofilled US numbers.
    const internationalPrefixes = [`011${dc}`, `00${dc}`];
    const prefixed = internationalPrefixes.find(prefix => digits.startsWith(prefix));
    if (prefixed && digits.length > prefixed.length) {
      return digits.slice(prefixed.length).slice(0, this.nationalMaxLength);
    }

    // If it looks like E.164 or trunked: starts with dial code and is longer than national length.
    if (digits.length > this.nationalMaxLength && digits.startsWith(dc)) {
      return digits.slice(dc.length).slice(0, this.nationalMaxLength);
    }

    // NANP convenience: drop leading '1' when length is 11
    if (dc === '1' && digits.length === this.nationalMaxLength + 1 && digits.startsWith('1')) {
      return digits.slice(1).slice(0, this.nationalMaxLength);
    }

    // Otherwise, just nationalize (and cap)
    return digits.slice(0, this.nationalMaxLength);
  }

  /** True if value is an E.164 string that includes our current dial code. */
  private shouldStripDialFrom(value: string): boolean {
    const dc = this.dialDigits();
    const d = this.onlyDigits(value);
    return d.length > this.nationalMaxLength && d.startsWith(dc);
  }

  private applyExternalValue(value: string) {
    // If external writes +1XXXXXXXXXX, convert to national
    if (this.shouldStripDialFrom(value)) {
      this.phoneNumber = this.toNationalDigits(value);
    } else {
      // External might write national digits; normalize/cap
      this.phoneNumber = this.toNationalDigits(value);
    }
  }

  private withoutExtension(value: string): string {
    return `${value}`.replace(/(?:ext\.?|extension|x|#)\s*\d+.*$/i, '');
  }

  private onlyDigits(s: string): string { return (s || '').replace(/\D/g, ''); }
  private dialDigits(): string { return this.dialCode.replace('+', '').trim(); }
}
